// Pure shaping logic for the step-5 affinity board. No React and no I/O, so
// every rule here is unit-testable in isolation — and several of them decide
// what gets written to the graph, which is why they live outside the
// components that render them.

import type { SynthesisAssignment } from '@/types/api';

export const BAND_ORDER = ['job', 'challenge', 'opportunity'] as const;
export type BandType = (typeof BAND_ORDER)[number];

// Band headings, spelled out rather than built as NODE_LABEL[type] + 's': that
// template rendered "Opportunitys", and a concatenation is invisible to the
// spell checker so nothing caught it.
export const BAND_PLURAL: Record<BandType, string> = {
  job: 'Jobs',
  challenge: 'Challenges',
  opportunity: 'Opportunities',
};

// Mirrors lambda/shared/synthesis-ai.ts. The server stays authoritative: it
// caps clusters per run and slices a promote's member list. These copies exist
// so the UI can say what happened instead of silently disagreeing with it.
// The frontend cannot import from lambda/ (separate tsconfig projects).
export const MAX_CLUSTERS_PER_RUN = 15;
export const MAX_MEMBERS_PER_CLUSTER = 60;

// Member tiles shown before "+N more". Six covers a small project's whole
// cluster, and bounds a 45-member one.
export const TILE_CAP = 6;

export interface RankableCluster {
  representativeType: BandType;
  respondentCount: number;
  itemCount: number;
  title: string;
}

export interface Band<T> {
  type: BandType;
  clusters: T[];
}

// Breadth first, because that is the number both the deck and step 7 sort on.
// Item count only breaks a tie: one talkative respondent inflates it.
export function sortClusters<T extends RankableCluster>(clusters: readonly T[]): T[] {
  return [...clusters].sort(
    (a, b) =>
      b.respondentCount - a.respondentCount ||
      b.itemCount - a.itemCount ||
      a.title.localeCompare(b.title),
  );
}

// Always returns all three bands, empty ones included. The cluster cap is
// global and applied in the model's output order, so a truncated run can leave
// a type with nothing in it; hiding that band would turn "the cap cut this off"
// into the much stronger claim "we found none of these".
export function groupIntoBands<T extends RankableCluster>(clusters: readonly T[]): Band<T>[] {
  return BAND_ORDER.map((type) => ({
    type,
    clusters: sortClusters(clusters.filter((c) => c.representativeType === type)),
  }));
}

export function runWasTruncated(clusterCount: number): boolean {
  return clusterCount >= MAX_CLUSTERS_PER_RUN;
}

// Filled fraction of the evidence meter, or null when there is no honest
// denominator to divide by (in which case the UI shows an item count instead).
// Clamped because the numerator and denominator are snapshotted at different
// moments.
export function meterProportion(count: number, total: number | undefined): number | null {
  if (!total || total <= 0) return null;
  return Math.min(1, count / total);
}

// An exclusion belongs to a theme, not to a finding. The synthesis prompt lets
// one finding support several themes, so a bare finding id as the key unticks it
// in every theme at once and quietly shrinks the respondentCount of whichever
// one is promoted next. Cluster ids are also fresh per run, so scoping the key
// this way expires stale exclusions when the board survives a re-synthesise.
// Composed here and nowhere else, so the read and write sides cannot drift.
export function excludedKey(clusterId: string, itemId: string): string {
  return `${clusterId}::${itemId}`;
}

// The member list a promote should send for one cluster. Order is preserved so
// the promoted node's evidence reads in the model's original grouping order.
export function includedMembers(
  clusterId: string,
  memberItemIds: readonly string[],
  excluded: ReadonlySet<string>,
): string[] {
  return memberItemIds.filter((id) => !excluded.has(excludedKey(clusterId, id)));
}

// The unticked members of one cluster, as bare item ids. The board holds a
// single composite-keyed set for the whole page; a container only ever needs its
// own slice of it to draw its tick marks.
export function excludedInCluster(
  clusterId: string,
  memberItemIds: readonly string[],
  excluded: ReadonlySet<string>,
): Set<string> {
  return new Set(memberItemIds.filter((id) => excluded.has(excludedKey(clusterId, id))));
}

export interface MergeUnion {
  memberItemIds: string[];
  count: number;
  overCap: boolean;
}

// The member list a merge should send: the union of the chosen clusters, minus
// anything the PM unticked. Exclusions win over the raw proposal lists, because
// someone who unticks a member and then merges plainly expects it gone. They are
// read per cluster: a finding unticked in one of the merged themes still counts
// if another merged theme carries it ticked.
//
// `overCap` exists because the server slices the union to
// MAX_MEMBERS_PER_CLUSTER. Left unflagged, a 62-member merge would persist 60
// while the button promised 62, so the UI blocks instead and points at
// deselection as the way out.
export function mergeUnion(
  clusters: readonly { id: string; memberItemIds: readonly string[] }[],
  excluded: ReadonlySet<string>,
): MergeUnion {
  const seen = new Set<string>();
  const memberItemIds: string[] = [];
  for (const cluster of clusters) {
    for (const id of cluster.memberItemIds) {
      if (excluded.has(excludedKey(cluster.id, id)) || seen.has(id)) continue;
      seen.add(id);
      memberItemIds.push(id);
    }
  }
  return {
    memberItemIds,
    count: memberItemIds.length,
    overCap: memberItemIds.length > MAX_MEMBERS_PER_CLUSTER,
  };
}

export interface MergeCandidate {
  id: string;
  // The title as the board currently holds it, which may be a PM edit.
  title: string;
  // The proposal's own title, used when the edit is blank.
  originalTitle: string;
  description?: string;
  representativeType: BandType;
  memberItemIds: string[];
}

export interface MergePlan {
  promote: { clusterId: string; title: string; description?: string; memberItemIds: string[] };
  dismissIds: string[];
}

// The entire merge decision, as data. Merge is this slice's one destructive
// action — it promotes the head cluster and dismisses the others — so the rules
// deciding whether it may run at all live here where they can be tested,
// rather than inline in a component where only a rendered test could reach
// them. A null plan means "do not issue any request".
//
// Refuses: fewer than two clusters, a mixed-type selection (the server rejects
// it), a union above the member cap (the server would silently slice it), and
// an empty union (the server 400s).
export function planMerge(
  chosen: readonly MergeCandidate[],
  excluded: ReadonlySet<string>,
): MergePlan | null {
  if (chosen.length < 2) return null;
  if (new Set(chosen.map((c) => c.representativeType)).size > 1) return null;

  const union = mergeUnion(chosen, excluded);
  if (union.overCap || union.memberItemIds.length === 0) return null;

  const [head, ...rest] = chosen;
  return {
    promote: {
      clusterId: head.id,
      title: head.title.trim() || head.originalTitle,
      description: head.description,
      memberItemIds: union.memberItemIds,
    },
    dismissIds: rest.map((c) => c.id),
  };
}

// Title comparison key, character-for-character the rule the server applies in
// lambda/shared/synthesis-ai.ts. It must not drift: if the two disagree, the
// board attaches an assignment to a group whose accept call then 404s.
export function canonicaliseTitle(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export interface AnchoredTheme {
  id: string;
  type: BandType;
  title: string;
  // The theme's current members, when known. Lets placeAssignments recognise
  // and drop a proposed assignment that would just re-attach a finding the
  // theme already has. Optional because not every call site tracks it.
  memberIds?: string[];
}

export interface AssignmentPlacement {
  byThemeId: Map<string, SynthesisAssignment[]>;
  orphans: SynthesisAssignment[];
}

// Route each proposed assignment to the anchored group it belongs on, keyed by
// canonical title AND the item's own type (a job and a challenge can share a
// title). Anything that does not resolve to exactly one theme becomes an
// orphan rather than a guess: the server refuses these too, 404 for none and
// 409 for several, and an orphan the board never rendered would be a silent
// loss instead of a visible error.
//
// An assignment whose item is already a member of the one matching theme is
// dropped instead of attached or orphaned: a re-synthesise sees the same
// finding again and has no way to know it is already on the theme, so it
// re-proposes it, and accepting that proposal is a harmless no-op the server
// de-duplicates. Rendering it anyway would just be a duplicate tile.
export function placeAssignments(
  assignments: readonly SynthesisAssignment[],
  themes: readonly AnchoredTheme[],
  typeOfItem: (itemId: string) => BandType | undefined,
): AssignmentPlacement {
  const byKey = new Map<string, AnchoredTheme[]>();
  for (const t of themes) {
    const key = `${canonicaliseTitle(t.title)}::${t.type}`;
    const bucket = byKey.get(key);
    if (bucket) bucket.push(t);
    else byKey.set(key, [t]);
  }

  const byThemeId = new Map<string, SynthesisAssignment[]>();
  const orphans: SynthesisAssignment[] = [];

  for (const a of assignments) {
    const itemType = typeOfItem(a.itemId);
    const matches = itemType
      ? byKey.get(`${canonicaliseTitle(a.existingThemeTitle)}::${itemType}`) ?? []
      : [];
    if (matches.length !== 1) {
      orphans.push(a);
      continue;
    }
    const target = matches[0];
    if (target.memberIds?.includes(a.itemId)) continue;
    const bucket = byThemeId.get(target.id);
    if (bucket) bucket.push(a);
    else byThemeId.set(target.id, [a]);
  }

  return { byThemeId, orphans };
}
